// Libs
import { put, takeEvery, takeLatest, call, select } from 'redux-saga/effects';

// Api
import http from '../../../api/http';

// Actions
import {
  GetSharedKitsSettings,
  SetSharedKitsSettings,
  GetSharedKitMessages,
  SetSharedKitMessages,
  GetSharedKits,
  SetSharedKits,
  GetSharedKitDetails,
  SetSharedKitDetails,
  CreateSharedKit,
  UpdateSharedKit,
  UpdateSharedKitItems,
  DeleteSharedKit,
  AddSharedKitToCart,
  SetSharedKitActionError,
  CleanSharedKitActionError,
} from '../../../actions/sharedKits';
import { SetLoading } from '../../../actions/loading';
import { SetProducts } from '../../../actions/products';
import { CloseModal } from '../../../actions/modal';
import { ClearApiError } from '../../../actions/apiError';
import { GetCartProducts } from '../../../actions/cart';

import handleApiError from '../../../sagas/handleApiError';

// Constants
import { SHARED_KITS_URLS, SUPPORT_URLS } from '../../../constants/urls';

// Selectors
import {
  productsObjSelector,
  allProductsSelector,
} from '../../../store/selectors';

// Utils
import { normalizeSharedKits, normalizeSharedKit } from '../utils/sharedKits';
import { parseSharedKitError } from '../utils/sharedKitErrors';

function* handleSharedKitActionError(e) {
  const { detail, fieldErrors } = parseSharedKitError(e);
  const firstFieldError = Object.values(fieldErrors)[0];

  yield put(SetSharedKitActionError(firstFieldError || detail));
}

function* saveKitProducts(products) {
  const allProducts = yield select(allProductsSelector);

  yield put(SetProducts({ ...allProducts, obj: products }));
}

function* getSharedKitsSettingsSaga() {
  try {
    const response = yield call(http.get, SHARED_KITS_URLS.getSettings);

    yield put(
      SetSharedKitsSettings({
        canCreate: !!response?.data?.can_create,
        subgroups: response?.data?.subgroups || [],
      }),
    );
  } catch (e) {
    yield call(handleApiError, e, 'getSharedKitsSettings');
  }
}

function* getSharedKitMessagesSaga() {
  try {
    const response = yield call(http.get, SUPPORT_URLS.getShippingNotice);

    yield put(
      SetSharedKitMessages({
        reorderMessage: response?.data?.shared_kit_reorder_message || '',
        addToMyKitsMessage:
          response?.data?.shared_kit_add_to_my_kits_message || '',
      }),
    );
  } catch (e) {
    yield call(handleApiError, e, 'getSharedKitMessages');
  }
}

function* getSharedKitsSaga() {
  try {
    yield put(SetLoading(true));
    const response = yield call(http.get, SHARED_KITS_URLS.getSharedKits);
    const productsObj = yield select(productsObjSelector);
    const { kits, products } = normalizeSharedKits(response?.data, productsObj);

    yield call(saveKitProducts, products);
    yield put(SetSharedKits(kits));
  } catch (e) {
    yield call(handleApiError, e, 'getSharedKits');
  } finally {
    yield put(SetLoading(false));
  }
}

function* getSharedKitDetailsSaga({ payload }) {
  try {
    yield put(SetLoading(true));
    const response = yield call(
      http.get,
      SHARED_KITS_URLS.sharedKitDetails(payload),
    );
    const productsObj = yield select(productsObjSelector);
    const { kit, products } = normalizeSharedKit(response?.data, productsObj);

    yield call(saveKitProducts, products);
    yield put(SetSharedKitDetails(kit));
  } catch (e) {
    yield call(handleApiError, e, 'getSharedKitDetails');
  } finally {
    yield put(SetLoading(false));
  }
}

function* createSharedKitSaga({ payload }) {
  try {
    const { kit, callback } = payload;
    yield put(CleanSharedKitActionError());
    yield call(http.post, SHARED_KITS_URLS.createSharedKit, kit);
    yield put(CloseModal());
    yield put(GetSharedKits());

    if (callback) {
      yield call(callback);
    }
  } catch (e) {
    yield call(handleSharedKitActionError, e);
  }
}

function* updateSharedKitSaga({ payload }) {
  try {
    const { kitId, kit, callback } = payload;
    yield put(CleanSharedKitActionError());
    const response = yield call(
      http.patch,
      SHARED_KITS_URLS.editSharedKit(kitId),
      kit,
    );
    const productsObj = yield select(productsObjSelector);
    const { kit: updatedKit, products } = normalizeSharedKit(
      response?.data,
      productsObj,
    );

    yield call(saveKitProducts, products);
    yield put(SetSharedKitDetails(updatedKit));
    yield put(CloseModal());
    yield put(GetSharedKits());

    if (callback) {
      yield call(callback);
    }
  } catch (e) {
    yield call(handleSharedKitActionError, e);
  }
}

function* updateSharedKitItemsSaga({ payload }) {
  const { kitId, products } = payload;

  try {
    yield put(CleanSharedKitActionError());
    const response = yield call(
      http.patch,
      SHARED_KITS_URLS.editSharedKit(kitId),
      {
        items: products.map(({ product, nums }) => ({
          product_id: product,
          quantity: nums,
        })),
      },
    );
    const productsObj = yield select(productsObjSelector);
    const { kit, products: kitProducts } = normalizeSharedKit(
      response?.data,
      productsObj,
    );

    yield call(saveKitProducts, kitProducts);
    yield put(SetSharedKitDetails(kit));
    yield put(GetSharedKits());
  } catch (e) {
    yield call(handleSharedKitActionError, e);
    yield put(GetSharedKitDetails(kitId));
  }
}

function* deleteSharedKitSaga({ payload }) {
  try {
    const { kitId, callback } = payload;
    yield put(ClearApiError());
    yield call(http.delete, SHARED_KITS_URLS.deleteSharedKit(kitId));
    yield put(CloseModal());
    yield put(GetSharedKits());

    if (callback) {
      yield call(callback);
    }
  } catch (e) {
    yield put(CloseModal());
    yield call(handleApiError, e, 'deleteSharedKit');
  }
}

function* addSharedKitToCartSaga({ payload }) {
  const {
    kitId,
    attendeeCount,
    pathname,
    onUnavailableProducts,
    navigateToCart,
  } = payload;

  try {
    yield put(CleanSharedKitActionError());
    const body = attendeeCount ? { attendee_count: attendeeCount } : {};
    const response = yield call(
      http.post,
      SHARED_KITS_URLS.addSharedKitToCart(kitId),
      body,
    );

    yield put(CloseModal());
    yield put(GetCartProducts(pathname || ''));

    const unavailableProductIds = response?.data?.unavailable_product_ids || [];

    if (unavailableProductIds.length) {
      yield call(onUnavailableProducts, unavailableProductIds);
      return;
    }

    yield call(navigateToCart);
  } catch (e) {
    yield call(handleSharedKitActionError, e);
  }
}

function* sharedKitsSaga() {
  yield takeEvery(GetSharedKitsSettings, getSharedKitsSettingsSaga);
  yield takeEvery(GetSharedKitMessages, getSharedKitMessagesSaga);
  yield takeEvery(GetSharedKits, getSharedKitsSaga);
  yield takeEvery(GetSharedKitDetails, getSharedKitDetailsSaga);
  yield takeEvery(CreateSharedKit, createSharedKitSaga);
  yield takeEvery(UpdateSharedKit, updateSharedKitSaga);
  yield takeLatest(UpdateSharedKitItems, updateSharedKitItemsSaga);
  yield takeEvery(DeleteSharedKit, deleteSharedKitSaga);
  yield takeEvery(AddSharedKitToCart, addSharedKitToCartSaga);
}

export default sharedKitsSaga;
export {
  handleSharedKitActionError,
  saveKitProducts,
  getSharedKitsSettingsSaga,
  getSharedKitMessagesSaga,
  getSharedKitsSaga,
  getSharedKitDetailsSaga,
  createSharedKitSaga,
  updateSharedKitSaga,
  updateSharedKitItemsSaga,
  deleteSharedKitSaga,
  addSharedKitToCartSaga,
};
